import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { collection, getDocs, limit, onSnapshot, orderBy, query, startAfter } from "firebase/firestore";
import {
  Activity,
  Clock3,
  Search,
  Circle,
  ChevronRight,
  Users,
  Wifi,
  WifiOff,
  Loader2,
} from "lucide-react";
import { db } from "../../firebase";
import AdminLayout from "../layout/AdminLayout";
import femaleNoImage from "../../Assets/femalenoimage.png";
import maleNoImage from "../../Assets/mennoimage.png";

const ONLINE_WINDOW_MS = 2 * 60 * 1000;
const PAGE_SIZE = 30;

function toDateLike(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatRelativeTime(value, nowMs = Date.now()) {
  const date = toDateLike(value);
  if (!date) return "Never seen";

  const diff = Math.max(0, nowMs - date.getTime());
  if (diff < 10 * 1000) return "just now";
  if (diff < 60 * 1000) return `${Math.max(1, Math.floor(diff / 1000))}s ago`;
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
}

function getLastActivityLabel(user) {
  if (!user?.lastActiveAt) return "No recorded activity";
  const nav = String(user.lastNavigationType || "").toLowerCase();
  if (nav === "browser-back-forward" || nav === "pop") return "Browser back/forward";
  if (nav === "push") return "New page";
  if (nav === "replace") return "Page replace";
  return "In-app activity";
}

const Presence = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [nowTick, setNowTick] = useState(Date.now());
  const loadMoreRef = useRef(null);

  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["adminPresencePage"],
    queryFn: async ({ pageParam = null }) => {
      const baseQuery = [collection(db, "profiles"), orderBy("lastActiveAt", "desc")];
      const q = pageParam
        ? query(...baseQuery, startAfter(pageParam), limit(PAGE_SIZE))
        : query(...baseQuery, limit(PAGE_SIZE));

      try {
        const snap = await getDocs(q);
        const docs = snap.docs;
        return {
          users: docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          nextCursor: docs.length === PAGE_SIZE ? docs[docs.length - 1] : null,
        };
      } catch {
        if (pageParam) return { users: [], nextCursor: null };
        const fallbackSnap = await getDocs(query(collection(db, "profiles"), limit(PAGE_SIZE)));
        const docs = fallbackSnap.docs;
        return {
          users: docs.map((doc) => ({ id: doc.id, ...doc.data() })),
          nextCursor: docs.length === PAGE_SIZE ? docs[docs.length - 1] : null,
        };
      }
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 30000,
    retry: 1,
  });

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowTick(Date.now());
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const liveQuery = query(collection(db, "profiles"), orderBy("lastActiveAt", "desc"), limit(PAGE_SIZE));

    const unsubscribe = onSnapshot(
      liveQuery,
      (snap) => {
        const liveUsers = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        queryClient.setQueryData(["adminPresencePage"], (oldData) => {
          const oldPages = oldData?.pages || [];
          const nextPages = oldPages.length
            ? [{ ...oldPages[0], users: liveUsers }, ...oldPages.slice(1)]
            : [{ users: liveUsers, nextCursor: snap.docs.length === PAGE_SIZE ? snap.docs[snap.docs.length - 1] : null }];

          return {
            pageParams: oldData?.pageParams || [null],
            pages: nextPages,
          };
        });
      },
      () => {},
    );

    return () => unsubscribe();
  }, [queryClient]);

  useEffect(() => {
    if (!loadMoreRef.current || !hasNextPage) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || isFetchingNextPage) return;
        fetchNextPage().catch(() => {});
      },
      { rootMargin: "300px 0px" },
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const users = useMemo(
    () => data?.pages.flatMap((page) => page.users) || [],
    [data],
  );

  const enrichedUsers = useMemo(() => {
    return users.map((user) => {
      const lastActiveAt = toDateLike(user.lastActiveAt);
      const online = lastActiveAt ? nowTick - lastActiveAt.getTime() <= ONLINE_WINDOW_MS : false;
      const lastSeen = formatRelativeTime(user.lastActiveAt, nowTick);
      return { ...user, online, lastSeen };
    });
    // NOTE: Do NOT sort here — Firestore already returns newest-active first.
    // Re-sorting across paginated results causes users to jump position on load more.
  }, [users, nowTick]);

  const onlineUsers = useMemo(() => enrichedUsers.filter((u) => u.online), [enrichedUsers]);
  const offlineUsers = useMemo(() => enrichedUsers.filter((u) => !u.online), [enrichedUsers]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return enrichedUsers.filter((user) => {
      if (filter === "online" && !user.online) return false;
      if (filter === "offline" && user.online) return false;
      if (!term) return true;
      return (
        (user.fullName || "").toLowerCase().includes(term) ||
        (user.kmProfileId || "").toLowerCase().includes(term) ||
        (user.mobileNumber || "").includes(term) ||
        (user.lastActivePage || "").toLowerCase().includes(term)
      );
    });
  }, [enrichedUsers, filter, search]);

  const stats = [
    { label: "Loaded", value: enrichedUsers.length, icon: Users, tint: "text-sky-700 bg-sky-50" },
    { label: "Online", value: onlineUsers.length, icon: Wifi, tint: "text-emerald-700 bg-emerald-50" },
    { label: "Offline", value: offlineUsers.length, icon: WifiOff, tint: "text-amber-700 bg-amber-50" },
    { label: "Never seen", value: enrichedUsers.filter((u) => !u.lastActiveAt).length, icon: Clock3, tint: "text-ink-700 bg-sand-100" },
  ];

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="section-subtle">Live monitoring</p>
          <h1 className="mt-1 font-display text-3xl font-bold text-ink-800">Presence</h1>
          <p className="mt-1 text-[13.5px] text-ink-400">
            Showing the latest active users first with paginated loading.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-[12px] font-semibold text-emerald-700">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          Updates every 30 seconds
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map(({ label, value, icon: Icon, tint }) => (
          <div key={label} className="card p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{label}</p>
                <h2 className="mt-2 font-display text-3xl font-bold text-ink-800">
                  {isLoading ? "—" : value.toLocaleString()}
                </h2>
              </div>
              <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${tint}`}>
                <Icon size={18} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 card p-4 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="input-group flex-1">
            <Search size={17} className="text-ink-400" />
            <input
              placeholder="Search by name, profile ID, page, or mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              />
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              { key: "all", label: "All" },
              { key: "online", label: "Online" },
              { key: "offline", label: "Offline" },
            ].map((item) => {
              const active = filter === item.key;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setFilter(item.key)}
                  className={`rounded-2xl px-4 py-2 text-[13px] font-semibold transition ${
                    active
                      ? "bg-brand-gradient text-white shadow-soft"
                      : "border border-sand-200 bg-white text-ink-700 hover:border-brand-600 hover:text-brand-600"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-sand-200 pt-3">
          <p className="text-[12px] text-ink-400">
            {filteredUsers.length} users shown from the latest loaded activity.
          </p>
        </div>
      </div>

      <div className="mt-5 overflow-hidden rounded-[28px] border border-sand-200 bg-white shadow-soft">
        <div className="border-b border-sand-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-brand-600" />
            <h2 className="font-display text-lg font-bold text-ink-800">User Presence</h2>
          </div>
          <p className="mt-1 text-[12px] text-ink-400">Card grid view</p>
        </div>

        {isLoading ? (
          <div className="grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton h-32 rounded-3xl" />
            ))}
          </div>
        ) : isError ? (
          <div className="p-8 text-center text-ink-400">Unable to load presence data.</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-10 text-center">
            <Users className="mx-auto text-ink-300" />
            <p className="mt-2 text-ink-400">No users found.</p>
          </div>
        ) : (
          <div className="grid gap-3 p-5 sm:grid-cols-2 xl:grid-cols-3">
            {filteredUsers.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => navigate(`/admin/users/${user.id}`)}
                className="group rounded-3xl border border-sand-200 bg-sand-50 p-4 text-left transition hover:border-brand-600 hover:bg-white hover:shadow-elevated"
              >
                <div className="flex items-start gap-3">
                  {user.hidePhotos || !user.imageUrls?.[0] ? (
                    <img
                      src={user.gender === "Female" ? femaleNoImage : maleNoImage}
                      alt={user.gender === "Female" ? "Female placeholder" : "Male placeholder"}
                      className="h-14 w-14 shrink-0 rounded-2xl object-cover object-center ring-1 ring-sand-200"
                    />
                  ) : (
                    <img
                      src={user.imageUrls[0]}
                      alt={user.fullName}
                      className="h-14 w-14 shrink-0 rounded-2xl object-cover object-center ring-1 ring-sand-200"
                    />
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="truncate font-semibold text-ink-800">{user.fullName || "No Name"}</h3>
                        <p className="truncate text-[12px] text-ink-400">
                          {user.kmProfileId || user.mobileNumber || "No ID"}
                        </p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[10.5px] font-bold ${
                          user.online ? "bg-emerald-50 text-emerald-700" : "bg-sand-100 text-ink-500"
                        }`}
                      >
                        <Circle size={8} className={user.online ? "fill-emerald-500 text-emerald-500" : "fill-ink-400 text-ink-400"} />
                        {user.online ? "Online" : "Offline"}
                      </span>
                    </div>

                    <div className="mt-3 grid gap-2 text-[11.5px] text-ink-500">
                      <p className="truncate">Current page: {user.lastActivePage || "-"}</p>
                      <p>Last seen: {user.lastSeen}</p>
                      <p>Activity: {getLastActivityLabel(user)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex items-center justify-between text-[12px] font-semibold text-brand-600">
                  <span>
                    {user.online
                      ? "Currently active"
                      : user.lastSeen === "Never seen"
                        ? "Never seen"
                        : `Offline for ${user.lastSeen}`}
                  </span>
                  <ChevronRight size={15} className="transition group-hover:translate-x-0.5" />
                </div>
              </button>
            ))}
            <div ref={loadMoreRef} className="h-2 sm:col-span-2 xl:col-span-3" />
          </div>
        )}

        <div className="flex items-center justify-between border-t border-sand-200 px-5 py-4">
          <p className="text-[12px] text-ink-400">
            {hasNextPage ? "Scroll to load more recent users." : "You have reached the end of the loaded activity list."}
          </p>
          {hasNextPage && (
            <button
              type="button"
              onClick={() => fetchNextPage().catch(() => {})}
              disabled={isFetchingNextPage}
              className="inline-flex items-center gap-2 rounded-2xl border border-sand-200 bg-white px-4 py-2 text-[13px] font-semibold text-ink-700 transition hover:border-brand-600 hover:text-brand-600 disabled:opacity-60"
            >
              {isFetchingNextPage ? <Loader2 size={14} className="animate-spin" /> : null}
              Load more
            </button>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Presence;
